import { TEventApplication } from "api/rpc/2024-04/facilityAdmin/event/eventApplication";
import React from "react";
import "./eventApplicationNotification.scss";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleCheck } from "@fortawesome/pro-regular-svg-icons";
import { useTranslation } from "react-i18next";
import moment from "moment";
import { ButtonNew as Button } from "components/buttonNew";

interface IProps {
    application: TEventApplication;
    onDelete: (id: number) => void;
    onEditOpen: (application: TEventApplication, key: "template" | "tee-sheet") => void;
}

const WEEKDAYS = [
    "Sun",
    "Mon",
    "Tues",
    "Wed",
    "Thurs",
    "Fri",
    "Sat",
];

export default function EventApplicationNotification(props: IProps) {
    const { application, onDelete, onEditOpen } = props;
    const { t } = useTranslation();

    function getNthWeekdayOfMonth(date: Date) {
        const year = date.getFullYear();
        const month = date.getMonth();
        const weekday = date.getDay(); // 0-6

        let count = 0;

        for (let d = 1; d <= date.getDate(); d++) {
            const current = new Date(year, month, d);
            if (current.getDay() === weekday) {
                count++;
            }
        }

        return count;
    }

    const weekIndex = getNthWeekdayOfMonth(new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString()));
    const ORDINALS = [t("first"), t("second"), t("third"), t("fourth"), t("last")];

    function occuranceType() {
        switch (application?.recurrence_type) {
            case "daily":
                return t("Daily");
            case "weekly":
                return t("Weekly on {{weekDayName}}", { weekDayName: moment(application?.start_date, "YYYY-MM-DD")?.format("dddd") });
            case "monthly":
                return t("Monthly on first {{weekDayName}}", { weekDayName: moment(application?.start_date, "YYYY-MM-DD")?.format("dddd") });
            case "annually":
                return t("Annually on {{monthDay}}", { monthDay: moment(application?.start_date, "YYYY-MM-DD")?.format("MMMM D") });
            case "every_week_day":
                return t("Every weekday (Mon - Fri)");
            case "custom":
                return t("Repeat every {{repeatAmount}} {{interval}}, ends {{endsType}} {{endsTime}} {{occurences}}", {
                    repeatAmount: application?.repeat_amount,
                    interval: application?.repeat_amount <= 1 ? application?.interval : application?.interval?.concat("s"),
                    endsType: application?.ends_type,
                    endsTime: application?.ends_type === "on" ? moment(application?.ends_on_date, "YYYY-MM-DD")?.format("ll") : application?.ends_after_occurrences,
                    occurences: application?.ends_type === "after" ? application?.ends_after_occurrences <= 1 ? "time" : "times" : "",
                });
            default:
                return "";
        }
    }
    return (
        <div className="ui-event-application-notification">
            <FontAwesomeIcon icon={faCircleCheck} style={{ width: "16.66px", height: "16.66px", marginTop: "5px", color: "#039855" }} />
            <div>
                <p className="ui-event-application-notification-title">{t("Event applied to {{eventType}} by {{adminName}}", { eventType: application?.template_id ? "template" : "tee sheet", adminName: application?.created_by?.full_name })}</p>
                {application?.template_id ? application?.application_type === "shotgun" && application?.shotgun_application_id ? (
                    <p className="ui-event-application-notification-primary">{t("Shotgun applied to {{templateName}} starting at", { templateName: application?.template?.name })} {moment(application?.start_time, "HH:mm")?.format("LT")}</p>
                ) : (
                    <p className="ui-event-application-notification-primary">{t("Applied to {{templateName}} from", { templateName: application?.template?.name })} {moment(application?.start_time, "HH:mm")?.format("LT")} - {moment(application?.end_time, "HH:mm")?.format("LT")}</p>
                ) : application?.application_type === "shotgun" ? (
                    <p className="ui-event-application-notification-primary">{t("Shotgun applied starting at")} {moment(application?.start_time, "HH:mm")?.format("LT")} {application?.recurrence_type === "custom" && application?.ends_type === "on" && application?.ends_on_date ? ` - ${moment(application?.ends_on_date, "YYYY-MM-DD")?.format("ll")} ` : null}
                    {application?.interval === "week" && application?.weekdays?.length > 0 ? ` • ${application?.weekdays?.map(dayNum => t(WEEKDAYS[dayNum]))?.join(", ")}` : null}
                    {application?.interval === "month" ? ` • ${application?.monthly_rule_type === "day_of_month" ? t("Monthly on {{day}}{{ending}}", {
                        day: new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate(),
                        ending: new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 1 ? "st"
                            : new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 2 ? "nd" :
                                new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 3 ? "rd" : "th",
                    }) : t("Monthly on {{ordinalName}} {{dayOfWeek}}", {
                        dayOfWeek: moment(application?.start_date, "YYYY-MM-DD")?.format("dddd"),
                        ordinalName: ORDINALS[weekIndex - 1] ?? t("last"),
                    })}` : null}
                    </p>
                ) : <p className="ui-event-application-notification-primary">{moment(application?.start_date, "YYYY-MM-DD")?.format("ll")} {application?.end_date && application?.recurrence_type !== "custom" && application?.recurrence_type !== "does_not_repeat" ? ` - ${moment(application?.end_date, "YYYY-MM-DD")?.format("ll")} ` : application?.recurrence_type === "custom" && application?.ends_type === "on" && application?.ends_on_date ? ` - ${moment(application?.ends_on_date, "YYYY-MM-DD")?.format("ll")} ` : null}
                    • {moment(application?.start_time, "HH:mm")?.format("LT")} - {moment(application?.end_time, "HH:mm")?.format("LT")} {application?.recurrence_type !== "does_not_repeat" ? ` • ${occuranceType()}` : null}
                    {application?.interval === "week" && application?.weekdays?.length > 0 ? ` • ${application?.weekdays?.map(dayNum => t(WEEKDAYS[dayNum]))?.join(", ")}` : null}
                    {application?.interval === "month" ? ` • ${application?.monthly_rule_type === "day_of_month" ? t("Monthly on {{day}}{{ending}}", {
                        day: new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate(),
                        ending: new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 1 ? "st"
                            : new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 2 ? "nd" :
                                new Date(moment(application?.start_date, "YYYY-MM-DD")?.toISOString())?.getDate() === 3 ? "rd" : "th",
                    }) : t("Monthly on {{ordinalName}} {{dayOfWeek}}", {
                        dayOfWeek: moment(application?.start_date, "YYYY-MM-DD")?.format("dddd"),
                        ordinalName: ORDINALS[weekIndex - 1] ?? t("last"),
                    })}` : null}
                </p>}

                <div className="ui-event-application-notification-button-group">
                    {application?.application_type === "regular" && !application?.shotgun_application_id ? (
                        <Button type="secondary" size="small" onClick={() => onEditOpen({ ...application }, application?.template_id ? "template" : "tee-sheet")}>{t("Edit")}</Button>
                    ) : null}
                    <Button className="p-0" type="text" size="small" onClick={() => onDelete(application?.id)}>{t("Delete")}</Button>
                </div>
            </div>
        </div>
    );
}